import React, { useState, useEffect, useMemo } from "react";
import {
	Tabs,
	Tab,
	TextField,
	Button,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Typography,
	Box,
	Dialog,
	DialogTitle,
	DialogContent,
	MenuItem,
	IconButton,
} from "@mui/material";
import { Search, ExpandMore, FileDownload, Close } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import BRANDING from "../config/branding";
import { API_URL } from '../config/api';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const getAmount = (network, sms) => {
	switch (network) {
		case "MTN":
		case "GLO":
		case "AIRTEL":
			return sms * 4.95;
		case "9MOBILE":
			return sms * 2.36;
		case "INTERNATIONAL":
			return sms * 160.0;
		default:
			return 0;
	}
};

export default function SummaryDashboard() {
	const [tab, setTab] = useState(0);
	const [searchCostCentre, setSearchCostCentre] = useState("");
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [filterOpen, setFilterOpen] = useState(false);
	const [tempFromDate, setTempFromDate] = useState(null);
	const [tempToDate, setTempToDate] = useState(null);
	const [tempCostCenter, setTempCostCenter] = useState("");
	const [tempCategory, setTempCategory] = useState("");
	const [tempSenderId, setTempSenderId] = useState("");

	const [appliedFromDate, setAppliedFromDate] = useState(null);
	const [appliedToDate, setAppliedToDate] = useState(null);
	const [appliedCostCenter, setAppliedCostCenter] = useState("");
	const [appliedCategory, setAppliedCategory] = useState("");
	const [appliedSenderId, setAppliedSenderId] = useState("");

	const allCategories = useMemo(() => {
		return Array.from(new Set(data.map((item) => item.category)));
	}, [data]);

	const allSenderIds = useMemo(() => {
		return Array.from(new Set(data.map((item) => item.senderId)));
	}, [data]);

	const allCostCenters = useMemo(() => {
		return Array.from(new Set(data.map((item) => item.costCenter))).filter(
			Boolean
		);
	}, [data]);

	useEffect(() => {
		async function fetchData() {
			const token = localStorage.getItem("token");
			try {
				const response = await fetch(
					`${API_URL}/get_summary.php`,
					{
						headers: { Authorization: `Bearer ${token}` },
					}
				);
				const result = await response.json();
				console.log(result);
				setData(result);
			} catch (err) {
				console.error("Error fetching data:", err);
				setError("Failed to load data");
			} finally {
				setLoading(false);
			}
		}

		fetchData();
	}, []);

	const filteredData = useMemo(() => {
		let filtered = data;

		if (searchCostCentre) {
			filtered = filtered.filter((item) =>
				item.costCenter.toLowerCase().includes(searchCostCentre.toLowerCase())
			);
		}

		if (appliedFromDate && appliedToDate) {
			filtered = filtered.filter((item) => {
				const itemDate = dayjs(item.date);
				return (
					itemDate.isAfter(dayjs(appliedFromDate).subtract(1, "day")) &&
					itemDate.isBefore(dayjs(appliedToDate).add(1, "day"))
				);
			});
		} else if (appliedFromDate) {
			filtered = filtered.filter((item) =>
				dayjs(item.date).isSameOrAfter(dayjs(appliedFromDate), "day")
			);
		} else if (appliedToDate) {
			filtered = filtered.filter((item) =>
				dayjs(item.date).isSameOrBefore(dayjs(appliedToDate), "day")
			);
		}

		if (appliedCostCenter) {
			filtered = filtered.filter((item) =>
				item.costCenter.toLowerCase().includes(appliedCostCenter.toLowerCase())
			);
		}

		if (appliedCategory) {
			filtered = filtered.filter((item) => item.category === appliedCategory);
		}

		if (appliedSenderId) {
			filtered = filtered.filter((item) => item.senderId === appliedSenderId);
		}

		if (tab === 1) {
			filtered = filtered
				.map((item) => ({
					...item,
					networks: item.networks.filter((n) => n.name === "INTERNATIONAL"),
				}))
				.filter((item) => item.networks.length > 0);
		}

		return filtered;
	}, [
		data,
		searchCostCentre,
		appliedFromDate,
		appliedToDate,
		appliedCostCenter,
		appliedCategory,
		appliedSenderId,
		tab,
	]);

	const handleApplyFilter = () => {
		setAppliedFromDate(tempFromDate);
		setAppliedToDate(tempToDate);
		setAppliedCostCenter(tempCostCenter);
		setAppliedCategory(tempCategory);
		setAppliedSenderId(tempSenderId);
		setFilterOpen(false);
	};

	const handleResetFilters = () => {
		setTempFromDate(null);
		setTempToDate(null);
		setTempCostCenter("");
		setTempCategory("");
		setTempSenderId("");
	};

	const exportToExcel = (data) => {
		const rows = [];
		let grandTotal = 0;

		data.forEach((item) => {
			const narration = "SMS ALERT MONTHLY VENDOR PAYMENT A/C";

			item.networks.forEach((net) => {
				const amount = getAmount(net.name, net.sms);
				grandTotal += amount;

				rows.push({
					Date: dayjs(item.date).format("YYYY-MM-DD"),
					Costcode: item.costCenter,
					Category: item.category || "",
					Network: net.name,
					Sms_Count: net.sms,
					Narration: narration,
					Amount: amount,
					Currency: "NGN",
				});
			});
		});

		rows.push({});
		rows.push({
			Date: "",
			Costcode: "",
			Narration: "TOTAL",
			Amount: grandTotal,
			Currency: "NGN",
		});

		const worksheet = XLSX.utils.json_to_sheet(rows);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "SMS Summary");

		const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
		saveAs(
			new Blob([wbout], { type: "application/octet-stream" }),
			`sms_summary_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx` // ✅ timestamped filename
		);
	};

	// const exportToExcel = (data) => {
	// 	const rows = [];
	// 	let grandTotal = 0;

	// 	data.forEach((item) => {
	// 		const narration = "SMS ALERT MONTHLY VENDOR PAYMENT A/C";

	// 		item.networks.forEach((net) => {
	// 			const amount = getAmount(net.name, net.sms);
	// 			grandTotal += amount;

	// 			rows.push({
	// 				Costcode: item.costCenter,
	// 				Category: item.category || "",
	// 				Network: net.name,
	// 				Sms_Count: net.sms,
	// 				Narration: narration,
	// 				Amount: amount,
	// 				Currency: "NGN",
	// 			});
	// 		});
	// 	});

	// 	rows.push({});
	// 	rows.push({
	// 		Costcode: "",
	// 		Narration: "TOTAL",
	// 		Amount: grandTotal,
	// 		Currency: "NGN",
	// 	});

	// 	const worksheet = XLSX.utils.json_to_sheet(rows);
	// 	const workbook = XLSX.utils.book_new();
	// 	XLSX.utils.book_append_sheet(workbook, worksheet, "SMS Summary");

	// 	const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
	// 	saveAs(
	// 		new Blob([wbout], { type: "application/octet-stream" }),
	// 		"sms_summary.xlsx"
	// 	);
	// };

	if (loading) return <Typography>Loading summary data...</Typography>;
	if (error) return <Typography color='error'>{error}</Typography>;

	return (
		<div className='p-6 bg-white rounded-lg shadow-sm border border-slate-200'>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				{/* Tabs */}
				<div className='flex justify-center mb-6'>
	<img
		src={BRANDING.logo}
		alt={BRANDING.shortName}
		className='h-16 w-auto object-contain'
	/>
</div>

<Typography
	variant='h4'
	align='center'
	sx={{
		color: BRANDING.primaryColor,
		fontWeight: 700,
		mb: 1,
	}}
>
	Message Summary
</Typography>

<Typography
	align='center'
	sx={{
		color: "#64748B",
		mb: 4,
	}}
>
	Communication reporting and usage analytics
</Typography>
				<Tabs
					value={tab}
					onChange={(_, val) => setTab(val)}
					className='mb-6'
					sx={{
						"& .MuiTabs-indicator": {
							backgroundColor: BRANDING.primaryColor,// Red-600
							height: "3px",
							borderRadius: "1.5px",
						},
						"& .MuiTabs-flexContainer": {
							backgroundColor: "white",
							borderRadius: "8px",
							padding: "4px",
							boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
						},
					}}
				>
					<Tab
						label='Summary'
						sx={{
						color: tab === 0 ? BRANDING.primaryColor : "#64748B",
							backgroundColor: tab === 0 ? "#EEF4FF" : "transparent", 
							fontWeight: tab === 0 ? "600" : "500",
							textTransform: "none",
							fontSize: "0.9rem",
							minWidth: "120px",
							padding: "8px 16px",
							borderRadius: "6px",
							margin: "2px",
							transition: "all 0.2s ease",
							"&:hover": {
								backgroundColor: tab === 0 ? "#fef2f2" : "#f3f4f6",
							},
						}}
					/>
					<Tab
						label='International'
						sx={{
							color: tab === 0 ? BRANDING.primaryColor : "#64748B",
							backgroundColor: tab === 0 ? "#EEF4FF" : "transparent",
							fontWeight: tab === 1 ? "600" : "500",
							textTransform: "none",
							fontSize: "0.9rem",
							minWidth: "120px",
							padding: "8px 16px",
							borderRadius: "6px",
							margin: "2px",
							transition: "all 0.2s ease",
							"&:hover": {
								backgroundColor: tab === 1 ? "#fef2f2" : "#f3f4f6",
							},
						}}
					/>
				</Tabs>

				{/* Controls - Search, Filter Button, Export Button */}
				<Box className='flex flex-wrap items-center justify-between gap-4 mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200'>
					{/* Search Cost Centre */}
					<TextField
						placeholder='Search cost centre'
						variant='outlined'
						size='small'
						value={searchCostCentre}
						onChange={(e) => setSearchCostCentre(e.target.value)}
						sx={{
							minWidth: "250px",
							flexGrow: 1,
							"& .MuiOutlinedInput-root": {
								borderRadius: "8px",
								backgroundColor: "#ffffff", // Light red background
								"&:hover .MuiOutlinedInput-notchedOutline": {
									borderColor: BRANDING.primaryColor, // Red-400
								},
								"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
									borderColor: BRANDING.primaryColor, // Red-600
								},
							},
						}}
						InputProps={{
							startAdornment: <Search sx={{ mr: 1, color: BRANDING.primaryColor }} />,
						}}
					/>

					<Box className='flex items-center gap-3'>
						{/* Filter Button */}
						<Button
							variant='outlined'
							sx={{
								backgroundColor: "white",
								color: BRANDING.primaryColor,
								borderColor: BRANDING.primaryColor,
								"&:hover": {
									backgroundColor: "#FEF2F2",// Light red background on hover
									borderColor: BRANDING.primaryColor, // Darker red border on hover
								},
								textTransform: "none",
								borderRadius: "8px",
								minWidth: "90px",
								padding: "8px 20px",
								fontWeight: "500",
							}}
							onClick={() => {
								setTempFromDate(appliedFromDate);
								setTempToDate(appliedToDate);
								setTempCostCenter(appliedCostCenter);
								setTempCategory(appliedCategory);
								setTempSenderId(appliedSenderId);
								setFilterOpen(true);
							}}
						>
							Filter
						</Button>
						{/* Export Button */}
						<Button
							variant='contained'
							startIcon={<FileDownload />}
							onClick={() => exportToExcel(filteredData)}
							sx={{
								background: `linear-gradient(135deg, ${BRANDING.primaryColor}, #B91C1C)`, // Red-600
								"&:hover": { background: "#B91C1C", }, // Red-700
								textTransform: "none",
								borderRadius: "8px",
								minWidth: "100px",
								padding: "8px 20px",
								fontWeight: "500",
								boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
							}}
						>
							Export
						</Button>
					</Box>
				</Box>

				{/* Table Header */}
				<Box className='flex justify-between w-full mb-4 py-3 px-4 bg-slate-50 border border-slate-200 rounded-lg shadow-sm'>
					<Typography
						variant='subtitle2'
						sx={{ width: "33%", fontWeight: "600", color: BRANDING.primaryColor }}
					>
						Date
					</Typography>
					<Typography
						variant='subtitle2'
						sx={{
							width: "33%",
							textAlign: "center",
							fontWeight: "600",
							color: BRANDING.primaryColor,
						}}
					>
						Total SMS sent
					</Typography>
					<Typography
						variant='subtitle2'
						sx={{
							width: "33%",
							textAlign: "right",
							fontWeight: "600",
							color: BRANDING.primaryColor,
						}}
					>
						Amount
					</Typography>
				</Box>

				{/* Table View (Accordions) */}
				<div className='space-y-3'>
					{filteredData.length > 0 ? (
						filteredData.map((item, idx) => {
							const totalSms = item.networks.reduce((acc, n) => acc + n.sms, 0);
							const totalAmount = item.networks.reduce(
								(acc, n) => acc + getAmount(n.name, n.sms),
								0
							);

							if (tab === 1 && totalSms === 0) {
								return null;
							}

							return (
								<Accordion
									key={idx}
									defaultExpanded={idx === 0}
									sx={{
										boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
										border: "1px solid #FECACA", // blue
										borderRadius: "12px",
										overflow: "hidden",
										backgroundColor: "white",
										"&:not(:last-child)": {
											marginBottom: "12px",
										},
										"&.Mui-expanded": {
											margin: "12px 0",
											boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
										},
										"& .MuiAccordionSummary-root": {
											padding: "16px 20px",
											minHeight: "60px",
											backgroundColor: "#FEF2F2", // Light red background
											"&:hover": {
												backgroundColor: "#FEE2E2",// Slightly darker red on hover
											},
										},
										"& .MuiAccordionDetails-root": {
											padding: "12px 20px 20px",
											backgroundColor: "white",
										},
									}}
								>
									<AccordionSummary
										expandIcon={
											<ExpandMore
												sx={{ color: BRANDING.primaryColor, fontSize: "1.5rem" }}
											/>
										}
										sx={{ flexDirection: "row-reverse" }}
									>
										<Box className='flex justify-between items-center w-full'>
											<Typography
												variant='body1'
												sx={{
													width: "33%",
													fontWeight: "600",
													color: "#374151",
												}}
											>
												{dayjs(item.date).format("MMMM D, YYYY")}
											</Typography>
											<Typography
												variant='body1'
												sx={{
													width: "33%",
													textAlign: "center",
													fontWeight: "500",
													color: "#374151",
												}}
											>
												{totalSms.toLocaleString()}
											</Typography>
											<Typography
												variant='body1'
												sx={{
													width: "33%",
													textAlign: "right",
													fontWeight: "600",
													color: "#16A34A",
												}}
											>
												₦
												{totalAmount.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}
											</Typography>
										</Box>
									</AccordionSummary>
									{item.networks.length > 0 && (
										<AccordionDetails>
											<div className='space-y-2 ml-4 border-l-2 border-red-200 pl-4'>
												{item.networks.map((n, subIdx) => (
													<Box
														key={subIdx}
														className='flex justify-between text-sm py-2 px-3 bg-red-50 rounded-lg'
													>
														<Typography
															variant='body2'
															sx={{
																width: "33%",
																fontWeight: "500",
																color: "#6b7280",
															}}
														>
															{n.name}
														</Typography>
														<Typography
															variant='body2'
															sx={{
																width: "33%",
																textAlign: "center",
																color: "#6b7280",
															}}
														>
															{n.sms.toLocaleString()}
														</Typography>
														<Typography
															variant='body2'
															sx={{
																width: "33%",
																textAlign: "right",
																fontWeight: "500",
																color: "#16A34A",
															}}
														>
															₦
															{getAmount(n.name, n.sms).toLocaleString(
																undefined,
																{
																	minimumFractionDigits: 2,
																	maximumFractionDigits: 2,
																}
															)}
														</Typography>
													</Box>
												))}
											</div>
										</AccordionDetails>
									)}
								</Accordion>
							);
						})
					) : (
						<div className='text-center py-12 px-4 bg-white rounded-lg border border-slate-200'>
							<Typography
								variant='body1'
								color='textSecondary'
								sx={{ fontSize: "1.1rem" }}
							>
								No data available for the selected filters.
							</Typography>
						</div>
					)}
				</div>

				{/* Filter Modal */}
				<Dialog
					open={filterOpen}
					onClose={() => setFilterOpen(false)}
					maxWidth='xs'
					fullWidth
					sx={{
						"& .MuiDialog-paper": {
							borderRadius: "16px",
							border: "1px solid #E2E8F0",
						},
					}}
				>
					<DialogTitle
						className='flex justify-between items-center'
						sx={{
							fontWeight: "600",
							fontSize: "1.2rem",
							pb: 1.5,
							pt: 3,
							px: 3,
							backgroundColor: "#FEF2F2",
							color: BRANDING.primaryColor
						}}
					>
						<span>Filter Options</span>
						<IconButton
							onClick={() => setFilterOpen(false)}
							sx={{
								p: 0.5,
								color: BRANDING.primaryColor,
								"&:hover": { backgroundColor: "#fecaca" },
							}}
						>
							<Close />
						</IconButton>
					</DialogTitle>
					<DialogContent
						dividers
						sx={{
							borderBottom: "none",
							px: 3,
							pt: 3,
							pb: 2,
							backgroundColor: "white",
						}}
						className='space-y-5'
					>
						<div>
							<Typography
								variant='body2'
								sx={{ mb: 2, fontWeight: "600", color: "#374151" }}
							>
								Date Range
							</Typography>
							<Box className='grid grid-cols-2 gap-3'>
								<DatePicker
									label='From'
									value={tempFromDate}
									onChange={(val) => setTempFromDate(val)}
									slotProps={{
										textField: {
											size: "small",
											fullWidth: true,
											sx: {
												"& .MuiOutlinedInput-root": {
													"&:hover .MuiOutlinedInput-notchedOutline": {
														borderColor: BRANDING.primaryColor
													},
													"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
														borderColor: BRANDING.primaryColor
													},
												},
											},
										},
									}}
								/>
								<DatePicker
									label='To'
									value={tempToDate}
									onChange={(val) => setTempToDate(val)}
									slotProps={{
										textField: {
											size: "small",
											fullWidth: true,
											sx: {
												"& .MuiOutlinedInput-root": {
													"&:hover .MuiOutlinedInput-notchedOutline": {
														borderColor: BRANDING.primaryColor
													},
													"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
														borderColor: BRANDING.primaryColor
													},
												},
											},
										},
									}}
								/>
							</Box>
						</div>

						<TextField
							select
							label='Cost Center'
							fullWidth
							size='small'
							value={tempCostCenter}
							onChange={(e) => setTempCostCenter(e.target.value)}
							sx={{
								"& .MuiOutlinedInput-root": {
									"&:hover .MuiOutlinedInput-notchedOutline": {
										borderColor: BRANDING.primaryColor
									},
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										borderColor: BRANDING.primaryColor
									},
								},
							}}
						>
							<MenuItem value=''>All</MenuItem>
							{allCostCenters.map((cc) => (
								<MenuItem key={cc} value={cc}>
									{cc}
								</MenuItem>
							))}
						</TextField>

						<TextField
							select
							label='Category'
							fullWidth
							size='small'
							value={tempCategory}
							onChange={(e) => setTempCategory(e.target.value)}
							sx={{
								"& .MuiOutlinedInput-root": {
									"&:hover .MuiOutlinedInput-notchedOutline": {
										borderColor: BRANDING.primaryColor,
									},
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										borderColor: BRANDING.primaryColor,
									},
								},
							}}
						>
							<MenuItem value=''>All</MenuItem>
							{allCategories.map((cat) => (
								<MenuItem key={cat} value={cat}>
									{cat}
								</MenuItem>
							))}
						</TextField>

						<TextField
							select
							label='Sender Id'
							fullWidth
							size='small'
							value={tempSenderId}
							onChange={(e) => setTempSenderId(e.target.value)}
							sx={{
								"& .MuiOutlinedInput-root": {
									"&:hover .MuiOutlinedInput-notchedOutline": {
										borderColor: BRANDING.primaryColor,
									},
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
										borderColor: BRANDING.primaryColor,
									},
								},
							}}
						>
							<MenuItem value=''>All</MenuItem>
							{allSenderIds.map((sid) => (
								<MenuItem key={sid} value={sid}>
									{sid}
								</MenuItem>
							))}
						</TextField>

						<Box className='flex justify-between items-center pt-4'>
							<Button
								variant='outlined'
								sx={{
									textTransform: "none",
									borderRadius: "8px",
									borderColor: BRANDING.primaryColor,
									color: BRANDING.primaryColor,
									fontWeight: "500",
									"&:hover": {
										backgroundColor: BRANDING.primaryColor,
										borderColor: BRANDING.primaryColor,
									},
								}}
								onClick={handleResetFilters}
							>
								Reset
							</Button>
							<Button
								variant='contained'
								sx={{
									background: `linear-gradient(135deg, ${BRANDING.primaryColor}, #B91C1C)`,
									"&:hover": {background: "#B91C1C" },
									textTransform: "none",
									borderRadius: "8px",
									fontWeight: "500",
									boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
								}}
								onClick={handleApplyFilter}
							>
								Apply Filter
							</Button>
						</Box>
					</DialogContent>
				</Dialog>
			</LocalizationProvider>
		</div>
	);
}

// pfacitiz

// citizenspfa
